import React, { useState, useEffect, useCallback } from 'react';
import { Spinner, Table } from 'reactstrap';
import { MdSearch, MdLogout, MdRefresh, MdArrowBack, MdArrowForward } from 'react-icons/md';
import { useFormContext } from '../context/FormContext';
import { getAllQuotes, getQuoteById } from './services/AdminService';
import { formatDate, formatCurrency } from './util/formatters';
import Session from './auth/Session';

const STEP_LABELS = {
    eligibility: 'Step 1 - Eligibility',
    dataPoints: 'Step 2 - Data Points',
    confirmDetails: 'Step 3 - Confirm Details',
    additionalParty: 'Step 3a - Additional Party',
    finalize: 'Step 4 - Finalize',
    sign: 'Step 5 - Sign',
    payment: 'Step 6 - Payment',
    paymentBank: 'Step 6a - Bank Details',
    paymentCard: 'Step 6b - Card Details',
    paymentReview: 'Step 7 - Review Payment',
    complete: 'Complete',
};

const STEP_ROUTES = {
    eligibility: '/signup/eligibility',
    dataPoints: '/signup/data-points',
    confirmDetails: '/signup/confirm-details',
    additionalParty: '/signup/additional-party',
    finalize: '/signup/finalize',
    sign: '/signup/sign',
    payment: '/signup/payment',
    paymentBank: '/signup/payment/bank',
    paymentCard: '/signup/payment/card',
    paymentReview: '/signup/payment/review',
    // complete: '/signup/complete',
    complete: '/dashboard',
};

function PaymentBadge({ status }) {
    const styles = {
        Paid: { background: '#f0faf5', color: '#2e7d52', border: '1px solid #4caf7d' },
        Declined: { background: '#fdf0f0', color: '#c0392b', border: '1px solid #e05c5c' },
        Pending: { background: '#fdf8f0', color: '#9a6000', border: '1px solid #d4a017' },
    };
    const s = styles[status] || styles.Pending;
    return (
        <span style={{
            ...s,
            padding: '3px 9px',
            borderRadius: '999px',
            fontSize: '0.75rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
        }}>
            {status || 'Pending'}
        </span>
    );
}

const QuoteDashboard = ({ history }) => {
    const { setFields, resetForm } = useFormContext();

    const [quotes, setQuotes] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [loading, setLoading] = useState(true);
    const [loadingRow, setLoadingRow] = useState(null);
    const [error, setError] = useState('');
    const [pageSize, setPageSize] = useState(15);

    const totalPages = Math.ceil(total / pageSize);

    // Fetch quotes
    const fetchQuotes = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const result = await getAllQuotes(search, page, pageSize);
            setQuotes(result.items || []);
            setTotal(result.total || 0);
        } catch (err) {
            setError('Failed to load quotes. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [search, page, pageSize]);

    useEffect(() => {
        fetchQuotes();
    }, [fetchQuotes]);

    // Search
    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        setSearch(searchInput.trim());
    };

    const handleClearSearch = () => {
        setSearchInput('');
        setSearch('');
        setPage(1);
    };

    // Row click - load quote into context and navigate
    const handleRowClick = async (quoteId, currentStep) => {
        setLoadingRow(quoteId);
        try {
            const result = await getQuoteById(quoteId);
            if (!result.success || !result.quote) {
                setError('Failed to load quote details.');
                return;
            }
 
            const q = result.quote;
 
            // Hydrate FormContext with all quote data
            // isReadOnly: true disables all inputs and API calls in the signup flow
            setFields({
                isReadOnly: true,
                QuoteId: q.id,
                currentStep: q.currentStep,
 
                // Property
                TargetAddressOne: q.targetAddressOne || '',
                TargetAddressTwo: q.targetAddressTwo || '',
                TargetCity: q.targetCity || '',
                TargetState: q.targetState || '',
                TargetZipCode: q.targetZipCode || '',
                TargetLat: q.targetLat || null,
                TargetLng: q.targetLng || null,
                TargetAddressFormatted: q.targetAddressFormatted || '',
                TargetHomeStatus: q.targetHomeStatus || '',
                ExposedHome: q.exposedHome || false,
                ExposedLandImprovements: q.exposedLandImprovements || false,
                ExposedHardscaping: q.exposedHardscaping || false,
                ExposedContents: q.exposedContents || false,
                ExposedValuables: q.exposedValuables || false,
                ExposedVehicles: q.exposedVehicles || false,
                ExposedLivingExpenses: q.exposedLivingExpenses || false,
                IsExposedValueOverMillion: q.isExposedValueOverMillion,
 
                // Contact
                FirstName: q.firstName || '',
                LastName: q.lastName || '',
                UserName: q.email || '',
                PhoneNumberPrimary: q.phoneNumberPrimary || '',
                PhoneNumberSecondary: q.phoneNumberSecondary || '',
                IsReceivingCriticalMessages: q.isReceivingCriticalMessages || false,
                OwnerType: q.ownerType || 'INDIVIDUAL',
 
                // Coverage
                CoverageAmount: q.coverageAmount || 0,
                EffectiveDate: q.effectiveDate || '',
 
                // Quote
                QuoteNumber: q.quoteNumber || '',
                SelectedTriggerType: q.selectedTriggerType || '',
                SelectedTotalPremium: q.selectedTotalPremium || 0,
 
                // Mailing
                ContactAddressOne: q.contactAddressOne || '',
                ContactAddressTwo: q.contactAddressTwo || '',
                ContactCity: q.contactCity || '',
                ContactState: q.contactState || '',
                ContactZipCode: q.contactZipCode || '',
                ContactCountry: q.contactCountry || 'USA',
                MailingSameAsProperty: q.mailingSameAsProperty || false,
 
                // Additional party - name comes back as a single string from DB
                // split on first space to approximate first/last for display
                HasAdditionalParties: q.hasAdditionalParties || false,
                AdditionalPartyType: q.additionalPartyType || '',
                AdditionalPartyFirstName: q.additionalPartyName?.split(' ')[0] || '',
                AdditionalPartyLastName: q.additionalPartyName?.split(' ').slice(1).join(' ') || '',
                AdditionalPartyAddressOne: q.additionalPartyAddressOne || '',
                AdditionalPartyAddressTwo: q.additionalPartyAddressTwo || '',
                AdditionalPartyCity: q.additionalPartyCity || '',
                AdditionalPartyState: q.additionalPartyState || '',
                AdditionalPartyZipCode: q.additionalPartyZipCode || '',
                AdditionalPartyCountry: q.additionalPartyCountry || 'USA',
 
                // Policy
                PolicyNumber: q.policyNumber || '',
                ExpiryDate: q.policyExpiryDate || '',
                PolicyTotalPremium: q.policyTotalPremium || null,
            });
 
            // Navigate to the step matching currentStep
            const route = STEP_ROUTES[q.currentStep] || '/signup/eligibility';
            history.push(route);
 
        } catch (err) {
            setError('Failed to load quote details.');
        } finally {
            setLoadingRow(null);
        }
    };

    // Logout
    const handleLogout = () => {
        Session.removeUserSession();
        resetForm();
        window.location.href = '/login';
    };

    const isAdmin = (Session.getRole() || '').includes('Admin');

    return (
        <div>
            <header style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem'}}>
                {isAdmin && (
                    <button className="dashboard__logout" onClick={() => history.push('/admin/users')}>
                        Users
                    </button>
                )}
                <button className="dashboard__logout" onClick={handleLogout}>
                    Log out
                    <MdLogout size={16} style={{ marginLeft: '4px' }} />
                </button>
            </header>
 
            {/* Page body */}
            <div>
                {/* Toolbar */}
                <div className="qd-toolbar">
                    <div style={{display: "flex", gap: "1rem", alignItems: 'flex-end'}}>
                        <form onSubmit={handleSearch} className="qd-search">
                            <div className="input-icon-wrap">
                                <span className="input-icon"><MdSearch size={16} /></span>
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    style={{ paddingLeft: '34px', margin: 0, width: '420px', height: '100%' }}
                                />
                            </div>
                            <button type="submit" className="btn" style={{ padding: '0.5rem 1rem' }}>
                                Search
                            </button>
                            {search && (
                                <button
                                    type="button"
                                    className="btn btn--inverse-cta"
                                    onClick={handleClearSearch}
                                    style={{ padding: '0.5rem 1rem' }}
                                >
                                    Clear
                                </button>
                            )}
                        </form>

                        {/* Adjust page size */}
                        <div>
                            {/* <p className="signup-field-label"><strong>Select page size.</strong></p> */}
                            <select
                            style={{margin: 0, width: '80px'}}
                            value={pageSize}
                            onChange={(e) => 
                            {
                                setPage(1);
                                setPageSize(Number(e.target.value))
                            }}>
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={15}>15</option>
                                <option value={20}>20</option>
                                <option value={25}>25</option>
                            </select>
                        </div>

                    </div>
                    <div>
                        <p className="qd-toolbar__count">
                            <strong>{total}</strong> quote{total !== 1 ? 's' : ''}
                            {search ? <span className="qd-toolbar__filter"> - filtered by "{search}"</span> : ''}
                        </p>
                    </div>
                </div>
 
                {/* Error */}
                {error && (
                    <p className="error-text" style={{ padding: '0 var(--space-xl)', marginBottom: 'var(--space-md)' }}>
                        {error}
                    </p>
                )}
 
                {/* Table area */}
                <div className="qd-table-area">
                    {loading ? (
                        <div className="dp-loading" style={{ padding: 'var(--space-3xl) 0' }}>
                            <Spinner />
                        </div>
                    ) : quotes.length === 0 ? (
                        <div className="qd-empty">
                            <p>{search ? `No quotes found for "${search}".` : 'No quotes yet.'}</p>
                        </div>
                    ) : (
                        <Table hover responsive className="qd-table">
                            <thead>
                                <tr>
                                    <th>Step</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Property Address</th>
                                    <th>Coverage</th>
                                    <th>Zip Code</th>
                                    <th>Total Premium</th>
                                    <th>Quote #</th>
                                    <th>Policy #</th>
                                    <th>Payment</th>
                                    <th>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {quotes.map((q) => (
                                    <tr
                                        key={q.id}
                                        onClick={() => handleRowClick(q.id, q.currentStep)}
                                        className={`qd-row ${loadingRow === q.id ? 'qd-row--loading' : ''}`}
                                        title="Click to view full quote"
                                    >
                                        <td>
                                            <span className="qd-step-badge">
                                                {STEP_LABELS[q.currentStep] || q.currentStep || '-'}
                                            </span>
                                        </td>
                                        <td>{[q.firstName, q.lastName].filter(Boolean).join(' ') || '-'}</td>
                                        <td className="qd-email">{q.email || '-'}</td>
                                        <td className="qd-address">{q.targetAddressFormatted || '-'}</td>
                                        <td>{formatCurrency(q.coverageAmount, '-', 0)}</td>
                                        <td className="qd-mono">{q.zipCode || '-'}</td>
                                        <td>{formatCurrency(q.totalPremium, '-', 0)}</td>
                                        <td className="qd-mono">{q.quoteNumber || '-'}</td>
                                        <td className="qd-mono">{q.policyNumber || '-'}</td>
                                        <td><PaymentBadge status={q.paymentStatus} /></td>
                                        <td className="qd-date">{formatDate(q.createdOn)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </div>
 
                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="qd-pagination">
                        <button
                            className="btn btn--inverse-cta"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                            style={{fontSize: '0.85rem', padding: '0.5rem 0.87rem'}}
                        >
                            <MdArrowBack /> Prev
                        </button>
                        <span className="qd-pagination__info">
                            Page {page} of {totalPages}
                        </span>
                        <button
                            className="btn btn--inverse-cta"
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            style={{fontSize: '0.85rem', padding: '0.5rem 0.87rem'}}
                        >
                            Next <MdArrowForward />
                        </button>
                    </div>
                )}
 
            </div>
        </div>
    );

};

export default QuoteDashboard;